import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ElementMaterialConfig } from '../types';

export interface ThreeDStudioState {
  // Model & Packaging Geometry
  selectedModel: string; // 'bottle' | 'can' | 'box' | 'pouch' | 'jar' | 'cup'
  boxStickerFace: 'all' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';
  stickerOffsetZ: number; // 3D lift thickness in mm (0 to 5)

  // Material Maps & Finishes
  selectedFinish: string; // 'gloss' | 'matte' | 'satin' | 'gold_foil' | 'silver_foil' | 'metallic'
  selectiveFinishColor: string;
  containerMaterial: string;
  containerColor: string;
  capMaterialId: string;
  capColor: string;
  useCustomMaterial: boolean;
  containerRoughness: number;
  containerMetalness: number;
  containerTransmission: number;
  containerIor: number;

  // Tactile Relief & Height Map Shader Settings
  embossMode: 'none' | 'embossed' | 'debossed' | 'spot_uv';
  reliefHeight: number; // mm
  tactileTargetArea: 'all' | 'text_logo' | 'graphics' | 'high_contrast';
  edgeSoftness: number; // px
  showCondensation: boolean;

  // UV Placement & Mapping
  uvScale: number;
  uvX: number;
  uvY: number;
  uvRotation: number;
  uvRepeatX: number;
  uvRepeatY: number;
  wrapAround: boolean;

  // Camera & Turntable
  cameraAngle: string; // 'isometric' | 'front' | 'back' | 'left' | 'right' | 'topdown' | 'macro'
  zoomLevel3D: number;
  turntableSpeed: number;
  autoRotate: boolean;

  // Studio Lighting Environment
  lightingPreset: string;
  backdropEnv: string;
  lightIntensity: number;
  lightColor: string;
  ambientLightEnabled: boolean;
  ambientIntensity: number;
  rimLightIntensity: number;
  rimLightColor: string;
  lightAngleX: number;
  lightAngleY: number;

  // Studio Stage & Tone Exposure
  toneMappingExposure: number;
  pedestalColor: string;
  pedestalRingColor: string;
  pedestalVisible: boolean;

  // Multi-Label Specifications
  enableMultiLabel: boolean;
  backLabelTitle: string;
  neckLabelText: string;

  // Per-Element Material & Finishes Setup
  elementMaterials: Record<string, ElementMaterialConfig>;
  selectedElementIdForMaterial: string | null;
}

export type EngineStatus = 'idle' | 'initializing' | 'ready' | 'error' | 'context_lost';
export type SceneLifecycle = 'unmounted' | 'mounting' | 'active' | 'paused' | 'disposed';

/**
 * Checks if WebGL is supported by the current browser and environment.
 */
export function isWebGLAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    console.warn('WebGL availability check threw an exception:', e);
    return false;
  }
}

export interface ThreeDContextType extends ThreeDStudioState {
  // Engine & Canvas Context Lifecycle Management
  engineStatus: EngineStatus;
  engineError: string | null;
  isWebglSupported: boolean;
  sceneLifecycle: SceneLifecycle;
  activeCanvas: HTMLCanvasElement | null;
  engineInitVersion: number;

  setEngineStatus: (status: EngineStatus, error?: string | null) => void;
  setSceneLifecycle: (lifecycle: SceneLifecycle) => void;
  setActiveCanvas: (canvas: HTMLCanvasElement | null) => void;
  retryEngineInit: () => void;

  // Update state functions
  update3DState: (updates: Partial<ThreeDStudioState>) => void;
  reset3DState: () => void;
  
  // Specific category updates for quick synchronization
  setMaterialFinish: (finishId: string, customColor?: string) => void;
  setTactileRelief: (mode: 'none' | 'embossed' | 'debossed' | 'spot_uv', height?: number, targetArea?: 'all' | 'text_logo' | 'graphics' | 'high_contrast') => void;
  setCameraAndModel: (model: string, angle?: string) => void;
  setLightingAndBackdrop: (preset: string, backdrop?: string) => void;

  // Element-level material controls
  setElementMaterial: (elementId: string, config: Partial<ElementMaterialConfig>) => void;
  clearElementMaterial: (elementId: string) => void;
  batchSetElementMaterials: (configs: Record<string, ElementMaterialConfig>) => void;
  setSelectedElementIdForMaterial: (elementId: string | null) => void;
}

export const defaultThreeDState: ThreeDStudioState = {
  selectedModel: 'bottle',
  boxStickerFace: 'all',
  stickerOffsetZ: 0.8,

  selectedFinish: 'gloss',
  selectiveFinishColor: '#f59e0b',
  containerMaterial: 'plastic_matte',
  containerColor: '#ffffff',
  capMaterialId: 'plastic_matte',
  capColor: '#0f172a',
  useCustomMaterial: false,
  containerRoughness: 0.85,
  containerMetalness: 0.05,
  containerTransmission: 0.0,
  containerIor: 1.5,

  embossMode: 'none',
  reliefHeight: 1.2,
  tactileTargetArea: 'all',
  edgeSoftness: 1.2,
  showCondensation: false,

  uvScale: 1.0,
  uvX: 50,
  uvY: 50,
  uvRotation: 0,
  uvRepeatX: 1,
  uvRepeatY: 1,
  wrapAround: false,

  cameraAngle: 'isometric',
  zoomLevel3D: 1.0,
  turntableSpeed: 1.0,
  autoRotate: false,

  lightingPreset: 'studio_dark',
  backdropEnv: 'studio_dark',
  lightIntensity: 1.8,
  lightColor: '#ffffff',
  ambientLightEnabled: true,
  ambientIntensity: 0.4,
  rimLightIntensity: 0.8,
  rimLightColor: '#a855f7',
  lightAngleX: 5,
  lightAngleY: 12,

  toneMappingExposure: 0.95,
  pedestalColor: '#1e293b',
  pedestalRingColor: '#f59e0b',
  pedestalVisible: true,

  enableMultiLabel: false,
  backLabelTitle: 'PRODUCT SPECIFICATIONS',
  neckLabelText: 'PREMIUM QUALITY • SEAL',

  elementMaterials: {},
  selectedElementIdForMaterial: null
};

const LOCAL_STORAGE_KEY_3D_STATE = 'vdp_3d_centralized_state_v3';

const ThreeDContext = createContext<ThreeDContextType | undefined>(undefined);

export const ThreeDProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [threeDState, setThreeDState] = useState<ThreeDStudioState>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_3D_STATE);
      if (saved) {
        return { ...defaultThreeDState, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Failed to load centralized 3D state from localStorage', e);
    }
    return defaultThreeDState;
  });

  // Centralized Engine, Canvas Context & Scene Lifecycle State
  const [engineStatus, setEngineStatusState] = useState<EngineStatus>('idle');
  const [engineError, setEngineError] = useState<string | null>(null);
  const [sceneLifecycle, setSceneLifecycleState] = useState<SceneLifecycle>('unmounted');
  const [activeCanvas, setActiveCanvasState] = useState<HTMLCanvasElement | null>(null);
  const [engineInitVersion, setEngineInitVersion] = useState<number>(0);
  const [isWebglSupported, setIsWebglSupported] = useState<boolean>(() => isWebGLAvailable());

  // Check WebGL availability upon mounting provider
  useEffect(() => {
    const supported = isWebGLAvailable();
    setIsWebglSupported(supported);
    if (!supported) {
      console.warn('[ThreeDProvider] WebGL is not supported or hardware acceleration is disabled in this browser.');
      setEngineStatusState('error');
      setEngineError('WebGL hardware acceleration is disabled or not supported in your browser.');
    }
  }, []);

  const setEngineStatus = (status: EngineStatus, error?: string | null) => {
    console.log(`[ThreeDProvider] Engine status changed: ${status}${error ? ` (${error})` : ''}`);
    setEngineStatusState(status);
    if (error !== undefined) {
      setEngineError(error);
    }
  };

  const setSceneLifecycle = (lifecycle: SceneLifecycle) => {
    console.log(`[ThreeDProvider] Scene lifecycle transitioned: ${lifecycle}`);
    setSceneLifecycleState(lifecycle);
  };

  const setActiveCanvas = (canvas: HTMLCanvasElement | null) => {
    setActiveCanvasState(canvas);
  };

  const retryEngineInit = () => {
    console.log('[ThreeDProvider] Retrying 3D rendering engine initialization...');
    const supported = isWebGLAvailable();
    setIsWebglSupported(supported);
    if (!supported) {
      setEngineStatusState('error');
      setEngineError('WebGL hardware acceleration is currently disabled in your browser.');
      return;
    }
    setEngineError(null);
    setEngineStatusState('initializing');
    setEngineInitVersion(prev => prev + 1);
  };

  // Auto-persist centralized 3D state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_3D_STATE, JSON.stringify(threeDState));
    } catch (e) {
      console.warn('Failed to persist 3D state to localStorage', e);
    }
  }, [threeDState]);

  const update3DState = (updates: Partial<ThreeDStudioState>) => {
    setThreeDState(prev => ({ ...prev, ...updates }));
  };

  const reset3DState = () => {
    setThreeDState(defaultThreeDState);
  };

  const setMaterialFinish = (finishId: string, customColor?: string) => {
    setThreeDState(prev => ({
      ...prev,
      selectedFinish: finishId,
      ...(customColor ? { selectiveFinishColor: customColor } : {})
    }));
  };

  const setTactileRelief = (
    mode: 'none' | 'embossed' | 'debossed' | 'spot_uv',
    height?: number,
    targetArea?: 'all' | 'text_logo' | 'graphics' | 'high_contrast'
  ) => {
    setThreeDState(prev => ({
      ...prev,
      embossMode: mode,
      ...(height !== undefined ? { reliefHeight: height } : {}),
      ...(targetArea !== undefined ? { tactileTargetArea: targetArea } : {})
    }));
  };

  const setCameraAndModel = (model: string, angle?: string) => {
    setThreeDState(prev => ({
      ...prev,
      selectedModel: model,
      ...(angle ? { cameraAngle: angle } : {})
    }));
  };

  const setLightingAndBackdrop = (preset: string, backdrop?: string) => {
    setThreeDState(prev => ({
      ...prev,
      lightingPreset: preset,
      backdropEnv: backdrop || preset
    }));
  };

  const setElementMaterial = (elementId: string, config: Partial<ElementMaterialConfig>) => {
    setThreeDState(prev => {
      const existing = prev.elementMaterials[elementId] || { finish: 'default' };
      return {
        ...prev,
        elementMaterials: {
          ...prev.elementMaterials,
          [elementId]: {
            ...existing,
            ...config
          } as ElementMaterialConfig
        }
      };
    });
  };

  const clearElementMaterial = (elementId: string) => {
    setThreeDState(prev => {
      const updated = { ...prev.elementMaterials };
      delete updated[elementId];
      return {
        ...prev,
        elementMaterials: updated
      };
    });
  };

  const batchSetElementMaterials = (configs: Record<string, ElementMaterialConfig>) => {
    setThreeDState(prev => ({
      ...prev,
      elementMaterials: {
        ...prev.elementMaterials,
        ...configs
      }
    }));
  };

  const setSelectedElementIdForMaterial = (elementId: string | null) => {
    setThreeDState(prev => ({
      ...prev,
      selectedElementIdForMaterial: elementId
    }));
  };

  return (
    <ThreeDContext.Provider
      value={{
        ...threeDState,
        engineStatus,
        engineError,
        isWebglSupported,
        sceneLifecycle,
        activeCanvas,
        engineInitVersion,
        setEngineStatus,
        setSceneLifecycle,
        setActiveCanvas,
        retryEngineInit,
        update3DState,
        reset3DState,
        setMaterialFinish,
        setTactileRelief,
        setCameraAndModel,
        setLightingAndBackdrop,
        setElementMaterial,
        clearElementMaterial,
        batchSetElementMaterials,
        setSelectedElementIdForMaterial
      }}
    >
      {children}
    </ThreeDContext.Provider>
  );
};

export const useThreeDContext = () => {
  const ctx = useContext(ThreeDContext);
  if (!ctx) {
    throw new Error('useThreeDContext must be used within a ThreeDProvider');
  }
  return ctx;
};
